import { supabase } from '@/lib/supabase';
import { isDepartureBookable, resolveArrivalTime } from '@/lib/schedule';
import type { Route, Schedule } from '@/types/database';

/**
 * A coach as a passenger is allowed to see it — the `buses_public` view.
 * Deliberately has no `name` or `registration_number`: the coach identity is
 * released by `rpc_my_departure_bus` once the reveal clock passes, and until
 * then even a direct API call must not expose it.
 */
export interface PublicBus {
  id: string;
  type: string;
  total_seats: number;
  amenities: unknown;
  status: string;
  fuel_type: string | null;
  /** Product tier, e.g. "Starline Gold". Safe at any time. */
  service_class: string;
}

// ============================================================
// Types — matches the existing BusResult interface from mockData
// so the UI components don't need changes
// ============================================================
export interface BusResult {
  id: string;
  scheduleId: string;
  routeId: string;
  from: string;
  to: string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  coachType: string;
  coachName: string;
  amenities: string[];
  availableSeats: number;
  totalSeats: number;
  fare: number;
  boardingPoints: string[];
  droppingPoints: string[];
  date: string;
}

export interface PopularRoute {
  id: string;
  from: string;
  to: string;
  distance: string;
  duration: string;
  basePrice: number;
  popularity: number;
}

// ============================================================
// Helper — convert duration_minutes to "5h 30m" format
// ============================================================
function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${h}h 00m`;
}

// ============================================================
// Helper — map bus.type to coach type label
// ============================================================
function mapBusType(bus: { type: string | null }): string {
  if (bus.type === 'Non-AC') return 'Non-AC';
  return 'AC';
}

// ============================================================
// 1. getPopularRoutes — fetch active routes sorted by schedule count
// ============================================================
export async function getPopularRoutes(): Promise<PopularRoute[]> {
  try {
    const { data: routes, error } = await supabase
      .from('routes')
      .select('*')
      .eq('status', 'active')
      .order('non_ac_fare', { ascending: false });

    if (error) {
      return [];
    }

    // Count schedules per route for popularity ranking
    const { data: schedules } = await supabase
      .from('schedules')
      .select('route_id')
      .eq('status', 'active');

    const scheduleCounts: Record<string, number> = {};
    schedules?.forEach((s: { route_id: string }) => {
      scheduleCounts[s.route_id] = (scheduleCounts[s.route_id] || 0) + 1;
    });

    return (routes || []).map((r: Route) => ({
      id: r.id,
      from: r.origin,
      to: r.destination,
      // Four of the five active routes carry `distance_km = 0`, which rendered
      // as a confident "5h 30m • 0 km". Both call sites already skip an empty
      // string, but this always returned a non-empty one so the guard never
      // fired. Say nothing rather than something false — filling in real
      // distances is the operator's call, not a QA fix.
      distance: r.distance_km > 0 ? `${r.distance_km} km` : '',
      duration: formatDuration(r.duration_minutes),
      basePrice: r.non_ac_fare,
      popularity: Math.min(100, (scheduleCounts[r.id] || 0) * 15 + 50),
    }))
    .sort((a, b) => b.popularity - a.popularity);
  } catch {
    return [];
  }
}

// ============================================================
// 2. searchTrips — find matching schedules for a route + date
// ============================================================
/** Exactly what `search_public_trips` returns — see its migration for the contract. */
interface SearchTripsPayload {
  routes: Route[];
  schedules: unknown[];
  counters: {
    route_id: string; name: string; custom_point_name?: string | null;
    is_boarding_allowed?: boolean; is_dropping_allowed?: boolean; sort_order?: number | null;
  }[];
  /** schedule_id -> booked seat count for the searched date. Aggregate only. */
  booked: Record<string, number>;
}

export async function searchTrips(
  from: string,
  to: string,
  date: string
): Promise<BusResult[]> {
  try {
    const trimFrom = from.trim();
    const trimTo = to.trim();

    // The day-of-week match moved into the RPC, where `p_date` is a real date
    // and `EXTRACT(DOW ...)` returns 0 = Sunday, the same basis JavaScript's
    // getDay() used. Nothing to get wrong in a timezone.

    // One round trip, not four.
    //
    // This used to be: routes, then schedules and counters, then bookings, then
    // booking_seats — each waiting on the one before it. From a phone in
    // Bangladesh that is roughly half a second to a second of pure network
    // latency before the database does any work.
    //
    // It also fixed a live bug. The bookings and booking_seats reads happened
    // in the browser, and neither table grants `anon` anything: as `anon`,
    // `select count(*) from bookings` returns 0 while `schedules` returns 24.
    // So every logged-out visitor got an empty booked count and saw
    // `availableSeats === totalSeats` — every coach looked completely empty
    // however full it really was. A signed-in user fared no better: they only
    // ever counted their own bookings.
    //
    // `search_public_trips` returns the same already-public routes, schedules and
    // counters, plus an aggregate booked-seat count per schedule. It is
    // SECURITY DEFINER solely for that count; no booking row, passenger detail
    // or seat label crosses the boundary, and the coach still comes from
    // `buses_public` so which physical bus runs a service stays hidden until
    // 15 minutes before departure via rpc_my_departure_bus.
    //
    // Every transformation below is unchanged and stays in TypeScript on
    // purpose — duplicating fare, duration and cutoff rules in SQL would put
    // them in two languages and guarantee they drift.
    const { data: search, error: searchError } = await (
      supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) =>
        Promise<{ data: SearchTripsPayload | null; error: unknown }>
    )('search_public_trips', { p_from: trimFrom, p_to: trimTo, p_date: date });

    if (searchError || !search) return [];

    const allRoutes = (search.routes ?? []) as Route[];
    if (allRoutes.length === 0) {
      // No routes found for this origin/destination pair
      return [];
    }

    const counters = search.counters ?? [];
    const bookedCounts: Record<string, number> = search.booked ?? {};

    const typedSchedules = (search.schedules ?? []) as unknown as (Schedule & { buses_public: PublicBus })[];
    const bookableSchedules = typedSchedules.filter((schedule) =>
      isDepartureBookable(date, schedule.departure_time),
    );
    if (bookableSchedules.length === 0) return [];

    // Build a map of route ID → route for quick lookup
    const routeMap: Record<string, Route> = {};
    allRoutes.forEach(r => { routeMap[r.id] = r; });

    // Group counter points by route
    const boardingByRoute: Record<string, string[]> = {};
    const droppingByRoute: Record<string, string[]> = {};
    type CounterPoint = { route_id: string; name: string; custom_point_name?: string | null; is_boarding_allowed?: boolean; is_dropping_allowed?: boolean };
    ((counters as CounterPoint[]) || []).forEach((c) => {
      const name = (c.custom_point_name && c.custom_point_name.trim()) || c.name;
      if (c.is_boarding_allowed) {
        if (!boardingByRoute[c.route_id]) boardingByRoute[c.route_id] = [];
        boardingByRoute[c.route_id].push(name);
      }
      if (c.is_dropping_allowed) {
        if (!droppingByRoute[c.route_id]) droppingByRoute[c.route_id] = [];
        droppingByRoute[c.route_id].push(name);
      }
    });

    // Transform to BusResult format — each schedule knows its route via route_id
    return bookableSchedules.map((schedule) => {
      const route = routeMap[schedule.route_id];
      if (!route) return null;

      const bus = schedule.buses_public;
      // Use fare_override if set, otherwise use route's AC/Non-AC fare directly
      const fare = schedule.fare_override
        || (bus.type === 'AC' ? route.ac_fare : route.non_ac_fare);

      const booked = bookedCounts[schedule.id] || 0;
      const available = Math.max(0, bus.total_seats - booked);

      // Parse amenities
      const amenities: string[] = Array.isArray(bus.amenities)
        ? bus.amenities as string[]
        : [];

      const boardingPts = boardingByRoute[route.id] || [];
      const droppingPts = droppingByRoute[route.id] || [];
      const fallbackBoarding = [`${route.origin} Terminal`, `${route.origin} Bypass`, `${route.origin} Central`];
      const fallbackDropping = [`${route.destination} Terminal`, `${route.destination} Main Stand`, `${route.destination} City Center`];

      return {
        id: schedule.id,
        scheduleId: schedule.id,
        routeId: route.id,
        from: route.origin,
        to: route.destination,
        departureTime: schedule.departure_time.substring(0, 5), // "HH:MM"
        arrivalTime: resolveArrivalTime(schedule.departure_time, schedule.arrival_time, route.duration_minutes),
        duration: formatDuration(route.duration_minutes),
        coachType: mapBusType(bus),
        coachName: bus.service_class,
        amenities,
        availableSeats: available,
        totalSeats: bus.total_seats,
        fare,
        boardingPoints: boardingPts.length > 0 ? boardingPts : fallbackBoarding,
        droppingPoints: droppingPts.length > 0 ? droppingPts : fallbackDropping,
        date,
      };
    }).filter(Boolean).sort((a: BusResult, b: BusResult) => a.departureTime.localeCompare(b.departureTime));
  } catch {
    return [];
  }
}

// ============================================================
// 3. getCities — distinct origin + destination city names
// ============================================================
let citiesCache: string[] | null = null;
let citiesCacheTime = 0;
const CITIES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function getCities(): Promise<string[]> {
  if (citiesCache && Date.now() - citiesCacheTime < CITIES_CACHE_TTL) return citiesCache;

  try {
    const { data: routes, error } = await supabase
      .from('routes')
      .select('origin, destination')
      .eq('status', 'active');

    if (error || !routes) {
      return citiesCache || [];
    }

    const cityMap = new Map<string, string>(); // lowercase → proper-cased
    routes.forEach((r: { origin: string; destination: string }) => {
      const o = r.origin.trim();
      const d = r.destination.trim();
      if (o && !cityMap.has(o.toLowerCase())) cityMap.set(o.toLowerCase(), o);
      if (d && !cityMap.has(d.toLowerCase())) cityMap.set(d.toLowerCase(), d);
    });

    citiesCache = Array.from(cityMap.values()).sort();
    citiesCacheTime = Date.now();
    return citiesCache;
  } catch {
    return [];
  }
}

// ============================================================
// 4. getRouteDetails — single route with its schedules and buses
// ============================================================
export async function getRouteDetails(routeId: string) {
  try {
    const { data: route, error } = await supabase
      .from('routes')
      .select(`
        *,
        schedules (
          *,
          buses_public (*)
        )
      `)
      .eq('id', routeId)
      .single();

    if (error) {
      return null;
    }

    return route;
  } catch {
    return null;
  }
}

// ============================================================
// 5. getBusTypes — distinct bus types for fleet display
// ============================================================
export async function getBusTypes() {
  try {
    const { data: buses, error } = await supabase
      .from('buses_public')
      .select('*')
      .eq('status', 'active')
      .order('total_seats', { ascending: true });

    if (error || !buses) {
      return [];
    }

    // A public fleet page lists what a passenger can *buy* — the tiers — not
    // the register of individual coaches. Three Gold coaches are one entry.
    const seen = new Set<string>();
    return (buses as unknown as PublicBus[]).flatMap(bus => {
      const key = bus.service_class + '|' + bus.total_seats;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{
        name: bus.service_class,
        type: mapBusType(bus),
        seats: bus.total_seats,
        amenities: Array.isArray(bus.amenities) ? bus.amenities as string[] : [],
      }];
    });
  } catch {
    return [];
  }
}
